import { AlertCircle, RotateCcw } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'

interface TranscriptViewerErrorBoundaryProps {
	children: React.ReactNode
}

interface TranscriptViewerErrorBoundaryState {
	error: Error | null
}

/**
 * Error boundary around the transcript viewer so a render error in the viewer
 * does not crash the whole project page. Shows a fallback with retry.
 */
export class TranscriptViewerErrorBoundary extends React.Component<
	TranscriptViewerErrorBoundaryProps,
	TranscriptViewerErrorBoundaryState
> {
	state: TranscriptViewerErrorBoundaryState = { error: null }

	static getDerivedStateFromError(error: Error): TranscriptViewerErrorBoundaryState {
		return { error }
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('TranscriptViewer error:', error, errorInfo)
	}

	render() {
		if (this.state.error) {
			return <TranscriptViewerErrorFallback onRetry={() => this.setState({ error: null })} />
		}
		return this.props.children
	}
}

function TranscriptViewerErrorFallback({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation()
	return (
		<div className="rounded-lg border border-[#ff3131]/30 bg-[#ff3131]/10 p-4" role="alert">
			<div className="flex items-start gap-3">
				<AlertCircle className="size-5 shrink-0 text-[#ff3131] mt-0.5" aria-hidden />
				<div className="flex-1 min-w-0">
					<h3 className="font-semibold text-[#ff3131] mb-1">
						{t('pages.projectDetail.artifactPreview.errorLoading')}
					</h3>
					<p className="text-sm text-red-700 dark:text-red-300 mb-4">
						{t('pages.projectDetail.transcript.errorLoading')}
					</p>
					<Button
						variant="outline"
						size="sm"
						onClick={onRetry}
						className="border-[#ff3131]/50 text-[#ff3131] hover:bg-[#ff3131]/10"
					>
						<RotateCcw className="size-3.5 mr-1.5" aria-hidden />
						{t('pages.projectDetail.artifactPreview.retry')}
					</Button>
				</div>
			</div>
		</div>
	)
}
